/**
 * IntegrationSetupModal Component
 *
 * Modal for setting up new integrations or editing existing ones.
 * Provides complete setup flow with test connection and save functionality.
 *
 * Features:
 * - Integration logo, name, and description display
 * - Dynamic form rendering via IntegrationForm
 * - Test connection before saving
 * - Success/error state handling
 * - Support for create and edit modes
 */

import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { Drawer } from '@praetorian-chariot/ui';
import React, { useCallback, useState } from 'react';

import { useTheme } from '@/app/ThemeContext';
import {
  useCreateIntegration,
  useTestIntegration,
  useUpdateIntegration,
} from '@/features/integrations/api';
import type { IntegrationInstance } from '@/features/integrations/types';
import type { SimpleIntegration } from '@/features/integrations/types/integration.types';
import { cn } from '@/utils/classname';

import { IntegrationForm } from './IntegrationForm';

export interface IntegrationSetupModalProps {
  /** Integration metadata with field definitions */
  integration: SimpleIntegration;
  /** Modal open state */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** Existing integration instance for edit mode */
  existingInstance?: IntegrationInstance;
  /** Success callback with created/updated instance */
  onSuccess?: (instance: IntegrationInstance) => void;
}

interface TestResult {
  success: boolean;
  message: string;
}

/**
 * IntegrationSetupModal - Modal for integration setup and testing
 */
export function IntegrationSetupModal({
  integration,
  isOpen,
  onClose,
  existingInstance,
  onSuccess,
}: IntegrationSetupModalProps) {
  const { currentTheme } = useTheme();
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  // React Query mutations
  const testMutation = useTestIntegration();
  const createMutation = useCreateIntegration();
  const updateMutation = useUpdateIntegration();

  /**
   * Handle test connection
   */
  const handleTest = useCallback(
    async (values: Record<string, string>) => {
      setTestResult(null);

      try {
        const result = await testMutation.mutateAsync({
          integrationId: integration.id,
          credentials: values,
        });

        setTestResult({
          success: result.success,
          message:
            result.message || result.success
              ? 'Connection successful! Your credentials are valid.'
              : 'Connection failed. Please check your credentials.',
        });
      } catch (error: any) {
        setTestResult({
          success: false,
          message:
            error.response?.data?.message ||
            error.message ||
            'Connection test failed. Please verify your credentials and try again.',
        });
      }
    },
    [integration.id, testMutation]
  );

  /**
   * Handle form submission (create or update)
   */
  const handleSubmit = useCallback(
    async (values: Record<string, string>) => {
      try {
        let instance: IntegrationInstance;

        if (existingInstance) {
          // Update existing integration
          instance = await updateMutation.mutateAsync({
            id: existingInstance.member,
            credentials: values,
          });
        } else {
          // Create new integration
          instance = await createMutation.mutateAsync({
            integrationId: integration.id,
            name: integration.name,
            credentials: values,
          });
        }

        // Call success callback
        if (onSuccess) {
          onSuccess(instance);
        }

        // Close modal
        onClose();
      } catch (error: any) {
        // Error handling - show error message
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'Failed to save integration. Please try again.';

        setTestResult({
          success: false,
          message: errorMessage,
        });
      }
    },
    [
      integration.id,
      existingInstance,
      onSuccess,
      onClose,
      createMutation,
      updateMutation,
    ]
  );

  /**
   * Handle modal close - reset state
   */
  const handleClose = useCallback(() => {
    setTestResult(null);
    onClose();
  }, [onClose]);

  return (
    <Drawer
      open={isOpen}
      onClose={handleClose}
      className="w-[800px] max-w-[90vw]"
    >
      <div className="h-full overflow-y-auto p-6">
        {/* Header with integration info */}
        <div className="mb-6 flex items-start gap-4">
          <img
            src={`/icons/${currentTheme}/${integration.logo}`}
            alt={integration.name}
            className="h-12 w-12 flex-shrink-0"
          />
          <div className="flex-1">
            <h2 className="text-chariot-text-primary text-2xl font-bold">
              {existingInstance ? 'Edit' : 'Setup'} {integration.name}
            </h2>
            <p className="text-chariot-text-secondary mt-1 text-sm">
              {integration.description}
            </p>
            {integration.help && (
              <a
                href={integration.help.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-chariot-brand-primary mt-2 inline-block text-sm hover:underline"
              >
                {integration.help.label} →
              </a>
            )}
          </div>
        </div>

        {/* Test result banner */}
        {testResult && (
          <div
            className={cn(
              'mb-6 flex items-start gap-3 rounded-md p-4',
              testResult.success
                ? 'border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                : 'border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
            )}
          >
            {testResult.success ? (
              <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600 dark:text-green-400" />
            ) : (
              <XCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" />
            )}
            <div className="flex-1">
              <p
                className={cn(
                  'text-sm font-medium',
                  testResult.success
                    ? 'text-green-800 dark:text-green-200'
                    : 'text-red-800 dark:text-red-200'
                )}
              >
                {testResult.success ? 'Success!' : 'Error'}
              </p>
              <p
                className={cn(
                  'mt-1 text-sm',
                  testResult.success
                    ? 'text-green-700 dark:text-green-300'
                    : 'text-red-700 dark:text-red-300'
                )}
              >
                {testResult.message}
              </p>
            </div>
          </div>
        )}

        {/* Integration form */}
        <IntegrationForm
          integration={integration}
          initialValues={existingInstance?.config || {}}
          onSubmit={handleSubmit}
          onCancel={handleClose}
          onTest={integration.validate ? handleTest : undefined}
          isLoading={createMutation.isPending || updateMutation.isPending}
          isTesting={testMutation.isPending}
          mode={existingInstance ? 'edit' : 'create'}
        />
      </div>
    </Drawer>
  );
}
